import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import {
  ChevronDown,
  Check,
  Database,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import { useClientLevel } from '../../hooks/useClientLevel';
import baseapi from '../../services/baseapi';
import { CustomDropdown } from '../../components/CustomDropdown ';
import DBTypeIcon from '../../components/DBTypeIcon';

// Import self-fetching subcomponents & DBSidebar
import { DBSidebar } from './components/DBSidebar';
import { OverviewView } from './components/OverviewView';
import { DatabasesView } from './components/DatabasesView';
import { StorageMaintenanceTab } from './components/StorageMaintenanceTab';
import { QueriesView } from './components/QueriesView';
import { UsersLoginsView } from './components/UsersLoginsView';
import { UserQueryStatisticsView } from './components/UserQueryStatisticsView';
import { SchemaView } from './components/SchemaView';
import { CallingServicesView } from './components/CallingServicesView';
import { ConfigurationView } from './components/ConfigurationView';
import { IndexesView } from './components/IndexesView';
import { OpenSearchIndicesView } from './components/OpenSearchIndicesView';
import { MongoCollectionsView } from './components/MongoCollectionsView';
import { MetricsTab } from './components/MetricsTab';
import { ConnectionsTab } from './components/ConnectionsTab';
import { HealthTab } from './components/HealthTab';
import { MSSQLTempDBView } from './components/MSSQLTempDBView';
import { MSSQLMemoryClerksView } from './components/MSSQLMemoryClerksView';
import { MSSQLSchedulersView } from './components/MSSQLSchedulersView';
import { MSSQLFileIOView } from './components/MSSQLFileIOView';
import { LogMonitorView } from './components/LogMonitorView';
import DBTopologyFlow from '../topology/DBTopologyFlow';

interface ClusterOption {
  label: string;
  value: string;
  dbType?: string;
  cluster_name?: string;
}

interface ClusterDropdownProps {
  clusters: ClusterOption[];
  activeCluster: string;
  onSelect: (val: string) => void;
  loading?: boolean;
}

const ClusterDropdown: React.FC<ClusterDropdownProps> = ({
  clusters,
  activeCluster,
  onSelect,
  loading,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 224 });

  const updateCoords = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 224),
      });
    }
  }, []);

  const toggleOpen = () => {
    if (!isOpen) {
      updateCoords();
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', updateCoords);
    window.addEventListener('scroll', updateCoords, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords, true);
    };
  }, [updateCoords]);

  const selectedCluster = clusters.find((c) => c.value === activeCluster) || clusters[0];

  return (
    <div className="relative inline-block text-left">
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleOpen}
        disabled={loading || clusters.length === 0}
        className="flex items-center justify-between gap-2.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg px-3 py-1.5 text-xs font-semibold text-white cursor-pointer transition-colors w-56 shadow-sm disabled:opacity-50 h-9"
      >
        <div className="flex items-center gap-2 truncate min-w-0">
          <DBTypeIcon dbType={selectedCluster?.dbType} className="w-4 h-4 shrink-0" />
          <span className="truncate">
            {loading ? 'Loading clusters...' : clusters.length === 0 ? 'No clusters available' : selectedCluster?.label || 'Select Cluster'}
          </span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && clusters.length > 0 && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute',
            top: `${coords.top}px`,
            left: `${coords.left}px`,
            width: `${coords.width}px`,
            zIndex: 99999,
          }}
          className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl overflow-hidden py-1 max-h-60 overflow-y-auto custom-scroll animate-in fade-in slide-in-from-top-1 duration-150"
        >
          {clusters.map((cluster) => {
            const isSelected = cluster.value === activeCluster;
            return (
              <button
                key={cluster.value}
                type="button"
                onClick={() => {
                  onSelect(cluster.value);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-xs font-semibold flex items-center justify-between gap-2 cursor-pointer transition-colors ${isSelected
                  ? 'bg-orange-500/10 text-orange-400 font-extrabold'
                  : 'text-gray-200 hover:bg-gray-800 hover:text-white'
                  }`}
              >
                <div className="flex items-center gap-2 truncate min-w-0">
                  <DBTypeIcon dbType={cluster.dbType} className="w-4 h-4 shrink-0" />
                  <span className="truncate">{cluster.label}</span>
                </div>
                {isSelected && <Check className="w-3.5 h-3.5 text-orange-400 shrink-0" />}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

export const DBMonitorDashboard: React.FC = () => {
  const level = useClientLevel();
  const [searchParams, setSearchParams] = useSearchParams();

  const [clusters, setClusters] = useState<ClusterOption[]>([]);
  const [clustersLoading, setClustersLoading] = useState<boolean>(false);
  const [activeCluster, setActiveCluster] = useState<string>(
    () => searchParams.get('cluster_id') || ''
  );
  const [timeRange, setTimeRange] = useState<string>('60');
  const [activeSidebar, setActiveSidebar] = useState<string>(
    () => searchParams.get('tab') || 'topology'
  );
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true);

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    const clusterFromUrl = searchParams.get('cluster_id');

    if (tabFromUrl && tabFromUrl !== activeSidebar) {
      setActiveSidebar(tabFromUrl);
    }
    if (clusterFromUrl && clusterFromUrl !== activeCluster) {
      setActiveCluster(clusterFromUrl);
    }
  }, [searchParams]);

  const handleSelectCluster = (val: string) => {
    setActiveCluster(val);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('cluster_id', val);
        if (!next.has('tab')) next.set('tab', activeSidebar);
        return next;
      },
      { replace: true }
    );
  };

  const handleSelectSidebar = (tabId: string) => {
    setActiveSidebar(tabId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (activeCluster) next.set('cluster_id', activeCluster);
        next.set('tab', tabId);
        return next;
      },
      { replace: true }
    );
  };

  const fetchClusters = useCallback(async () => {
    setClustersLoading(true);
    try {
      const params: any = {};
      if (level?.id) {
        params.client_id = level.id;
      }
      const res = await baseapi.get('/db_monitor_dashboard/clusters', { params });
      const rawData =
        res.data?.data || res.data?.clusters || (Array.isArray(res.data) ? res.data : []);
      if (Array.isArray(rawData) && rawData.length > 0) {
        const formatted = rawData.map((item: any) => ({
          label: item.cluster_name || item.name || String(item.cluster_id || item.id),
          value: String(item.cluster_id || item.id || item.value),
          dbType: item.db_type || item.dbType || item.engine || item.type || 'postgres',
          cluster_name: item.cluster_name || item.name || String(item.cluster_id || item.id),
        }));
        setClusters(formatted);

        const currentUrlCluster = new URLSearchParams(window.location.search).get('cluster_id');
        const matched = formatted.find((c) => c.value === currentUrlCluster);
        const selectedCluster = matched ? matched.value : formatted[0].value;

        setActiveCluster((prev) => (prev ? prev : selectedCluster));

        // Sync URL initial cluster_id if not present
        if (!currentUrlCluster) {
          const next = new URLSearchParams(window.location.search);
          next.set('cluster_id', selectedCluster);
          if (!next.has('tab')) next.set('tab', 'topology');
          window.history.replaceState(null, '', `?${next.toString()}`);
        }
      } else {
        setClusters([]);
        setActiveCluster('');
      }
    } catch (err) {
      console.warn('Failed to fetch clusters:', err);
      setClusters([]);
      setActiveCluster('');
    } finally {
      setClustersLoading(false);
    }
  }, [level?.id]);

  useEffect(() => {
    fetchClusters();
  }, [fetchClusters]);

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const timeOptions = [
    { label: 'Past 15 Minutes', value: '15' },
    { label: 'Past 1 Hour', value: '60' },
    { label: 'Past 4 Hours', value: '240' },
    { label: 'Past 12 Hours', value: '720' },
    { label: 'Past 24 Hours', value: '1440' },
    { label: 'Past 7 Days', value: '10080' },
  ];

  const currentClusterObj = clusters.find((c) => c.value === activeCluster);
  const currentDbType = currentClusterObj?.dbType;
  const currentClusterName = currentClusterObj?.cluster_name || currentClusterObj?.label || '';

  const isMongoDB = ['mongodb', 'mongo'].includes((currentDbType || '').toLowerCase());

  useEffect(() => {
    if (!currentDbType) return;
    const dbLower = currentDbType.toLowerCase();
    const isES = ['opensearch', 'elasticsearch'].includes(dbLower);
    const isMongo = ['mongodb', 'mongo'].includes(dbLower);
    const isMSSQL = ['mssql', 'sqlserver', 'sql server', 'sql_server'].includes(dbLower) || dbLower.includes('mssql');
    const isMySQL = ['mysql', 'mariadb'].includes(dbLower) || dbLower.includes('mysql');

    if (isES && ['blocking', 'databases', 'indexes', 'storage', 'blocks', 'vacuums',].includes(activeSidebar)) {
      setActiveSidebar('overview');
    } else if (isMongo && ['esindices', 'storage', 'blocks', 'vacuums',].includes(activeSidebar)) {
      setActiveSidebar('overview');
    } else if (!isES && activeSidebar === 'esindices') {
      setActiveSidebar('overview');
    } else if (!isMSSQL && (activeSidebar === 'tempdb' || activeSidebar === 'memory')) {
      setActiveSidebar('overview');
    } else if (!isMSSQL && !isMySQL && activeSidebar === 'schedulers') {
      setActiveSidebar('overview');
    }
  }, [currentDbType, activeSidebar]);

  if (clustersLoading) {
    return (
      <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-[2px] z-20 flex items-center justify-center rounded-xl min-h-[400px]">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    )
  }

  return (
    <div className="bg-gray-900 text-gray-100 h-[calc(100vh-1rem)] flex flex-col overflow-hidden p-4">
      {/* 100% Fixed Header Banner */}
      <div className="shrink-0 bg-gray-900 pb-2 border-b border-gray-700">
        <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xl backdrop-blur-md">
          {/* Heading */}
          <div className="flex items-center gap-3 shrink-0">
            <div className="w-9 h-9 rounded bg-orange-600/20 border border-orange-500/30 flex items-center justify-center text-white p-1.5">
              {clusters.length > 0 && currentDbType ? (
                <DBTypeIcon dbType={currentDbType} className="w-5 h-5" />
              ) : (
                <Database className="w-5 h-5 text-orange-400" />
              )}
            </div>
            <div>
              <h1 className="text-base font-bold text-white tracking-wide whitespace-nowrap">Database Monitoring</h1>
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center gap-2.5 shrink-0">
            {/* Refresh Button - Hidden on Topology Tab */}
            {activeSidebar !== 'topology' && (
              <button
                onClick={handleRefresh}
                className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700 rounded-lg transition-colors"
                title="Refresh active tab"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            )}
            {/* Cluster Selection Dropdown */}
            <ClusterDropdown
              clusters={clusters}
              activeCluster={activeCluster}
              onSelect={handleSelectCluster}
              loading={clustersLoading}
            />

            {/* Time Filter Dropdown */}
            <CustomDropdown
              data={timeOptions}
              selectedValue={timeRange}
              onSelect={(val) => setTimeRange(val)}
              width="w-36"
              placeholder="Time Range"
              buttonHeight={9}
            />


          </div>
        </header>
      </div>

      {/* Layout Row (Fixed Sidebar + Scrollable Main Content) */}
      {!clustersLoading && clusters.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center bg-gray-900/60 border border-gray-800 rounded-xl p-8 text-center mt-2.5 shadow-2xl">
          <div className="p-4 rounded-full bg-gray-800/80 border border-gray-700/60 mb-3 text-amber-400">
            <Database className="w-10 h-10" />
          </div>
          <h2 className="text-lg font-bold text-white mb-1">No Clusters Available</h2>
          <p className="text-xs text-gray-400 max-w-md">
            No database clusters are configured or detected for this client environment. Please register or connect a database cluster to enable database monitoring.
          </p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col lg:flex-row gap-4 mt-2.5 min-h-0 overflow-hidden">
          {/* Left Sidebar */}
          <div className={`shrink-0 overflow-y-auto custom-scroll pr-1 border-r border-gray-800 transition-[width] duration-300 ease-in-out ${isSidebarOpen ? 'w-full lg:w-52' : 'w-full lg:w-14'
            }`}>
            <DBSidebar
              activeSidebar={activeSidebar}
              onSelectSidebar={handleSelectSidebar}
              isOpen={isSidebarOpen}
              onToggleOpen={() => setIsSidebarOpen(!isSidebarOpen)}
              dbType={currentDbType}
            />
          </div>

          {/* Main Content Area */}
          <main className={`flex-1 min-w-0 ${activeSidebar === 'topology' ? 'h-full flex flex-col min-h-0 overflow-hidden pr-2' : 'overflow-y-auto custom-scroll space-y-3.5 pr-2 pb-12'}`}>
            {activeSidebar === 'overview' && (
              <OverviewView
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'esindices' && (
              <OpenSearchIndicesView
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
              />
            )}

            {activeSidebar === 'databases' && (
              isMongoDB ? (
                <MongoCollectionsView
                  activeCluster={activeCluster}
                  timeRange={timeRange}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                />
              ) : (
                <DatabasesView
                  activeCluster={activeCluster}
                  timeRange={timeRange}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                  dbType={currentDbType}
                />
              )
            )}

            {activeSidebar === 'tempdb' && (
              <MSSQLTempDBView
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'memory' && (
              <MSSQLMemoryClerksView
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'schedulers' && (
              <MSSQLSchedulersView
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'file_io' && (
              <MSSQLFileIOView
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'indexes' && (
              <IndexesView
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
              />
            )}

            {(activeSidebar === 'storage' || activeSidebar === 'blocks' || activeSidebar === 'vacuums') && (
              <StorageMaintenanceTab
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'blocking' && (
              <QueriesView
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'users' && (
              <UsersLoginsView
                activeCluster={activeCluster}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'user_queries' && (
              <UserQueryStatisticsView
                activeCluster={activeCluster}
                clientId={level?.id}
                refreshKey={refreshKey}
              />
            )}

            {activeSidebar === 'schema' && (
              <SchemaView
                activeCluster={activeCluster}
                clientId={level?.id}
                refreshKey={refreshKey}
              />
            )}

            {activeSidebar === 'calling' && <CallingServicesView />}

            {activeSidebar === 'topology' && (
              <div className="w-full h-full flex-1 min-h-0 pb-20">
                <DBTopologyFlow clusterId={activeCluster} />
              </div>
            )}

            {activeSidebar === 'metrics' && (
              <MetricsTab
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'connections' && (
              <ConnectionsTab
                activeCluster={activeCluster}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'health' && (
              <HealthTab
                activeCluster={activeCluster}
                clientId={level?.id}
                refreshKey={refreshKey}
              />
            )}

            {(activeSidebar === 'logs' || activeSidebar === 'log_monitor') && (
              <LogMonitorView
                activeCluster={activeCluster}
                clusterName={currentClusterName}
                clientId={level?.id}
                refreshKey={refreshKey}
              />
            )}

            {activeSidebar === 'configuration' && (
              <ConfigurationView
                activeCluster={activeCluster}
                clientId={level?.id}
                refreshKey={refreshKey}
              />
            )}
          </main>
        </div>
      )}
    </div>
  );
};

export default DBMonitorDashboard;
